import { useEffect, useRef, useState } from 'react'
import { api, type Area } from '../lib/api.js'
import { getPosition, initialMapCentre } from '../lib/geo.js'
import { t } from '../lib/i18n.js'
import { TILE, project, tileUrl, unproject } from '../lib/tiles.js'
import { Check, Minus, Navigation, Plus, Spinner } from './icons.js'

/**
 * Choosing where to meet, by pointing at it.
 *
 * A map rather than a text field, because "somewhere around here" is what
 * people actually mean and a postcode is only a proxy for it. One gesture, no
 * keyboard — which matters most on the screen §11 says decides the funnel.
 *
 * Drawn as plain `<img>` tiles, NOT MapLibre. MapLibre v6 requires WebGL2,
 * which is missing on plenty of real machines — remote sessions, older GPUs,
 * hardened browser profiles — and when it is missing the map renders nothing
 * at all. An image grid has no such failure mode: it paints wherever a browser
 * can show a picture, costs no library, and needs nothing initialised. The
 * venue card's map has always fallen back to exactly this, which is why that
 * one rendered when this one did not.
 *
 * The pin never moves: it sits fixed at the centre and the MAP moves under it.
 * Dragging a marker on a phone fights with panning and loses.
 */

/** Close enough to see streets, wide enough to recognise the district. */
const DEFAULT_ZOOM = 14
const MIN_ZOOM = 11
const MAX_ZOOM = 17

/** Let a pan settle before asking what is underneath. */
const SETTLE_MS = 400

interface Props {
  busy?: boolean | undefined
  onPick: (area: Area) => void
}

export function AreaPicker({ busy, onPick }: Props) {
  /**
   * Opened on the best guess available without asking anyone anything — a
   * remembered origin, else the browser's timezone, else Vienna. Nothing is
   * submitted from this, so a wrong guess costs a pan, not a bad meeting.
   */
  const [centre, setCentre] = useState(() => initialMapCentre())
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [area, setArea] = useState<Area | null>(null)
  const [naming, setNaming] = useState(false)
  const [locating, setLocating] = useState(false)

  /** Live drag state. A ref, because a pointermove must not re-render. */
  const drag = useRef<{ id: number; x: number; y: number } | null>(null)
  /**
   * The current zoom, readable from inside a handler.
   *
   * `panBy` runs in a closure created when the handler was attached, so it
   * would otherwise convert pixels using whatever zoom was current then — a
   * pan immediately after zooming would move the map by the wrong amount.
   */
  const zoomRef = useRef(DEFAULT_ZOOM)
  zoomRef.current = zoom
  /** Only the newest lookup may write; a slow answer must not label a new pin. */
  const lookup = useRef(0)
  const settle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Name whatever the pin has come to rest over.
  useEffect(() => {
    const at = { lat: centre.lat, lng: centre.lng }

    clearTimeout(settle.current)
    settle.current = setTimeout(() => {
      const mine = ++lookup.current
      setNaming(true)
      void api
        .areaAt(at)
        .then((found) => {
          if (lookup.current !== mine) return
          setArea(found)
          setNaming(false)
        })
        .catch(() => {
          if (lookup.current !== mine) return
          // No name is not no place. Keep the pin, drop the label.
          setArea(null)
          setNaming(false)
        })
    }, SETTLE_MS)

    return () => clearTimeout(settle.current)
  }, [centre.lat, centre.lng])

  /** Move the map by a pixel delta. */
  function panBy(dx: number, dy: number) {
    const z = zoomRef.current
    setCentre((c) => {
      const p = project(c.lat, c.lng, z)
      // Continuous tile-space position, shifted by the drag.
      const fx = p.x + p.offsetX / TILE - dx / TILE
      const fy = p.y + p.offsetY / TILE - dy / TILE
      const next = unproject(fx, fy, z)
      // Past the poles there is no map, and going there strands the user on
      // blank tiles with no way to tell which way is back.
      return { lat: Math.max(-85, Math.min(85, next.lat)), lng: next.lng }
    })
  }

  /**
   * Jump to where the device says we are.
   *
   * Behind a button rather than fired on mount: it is the only source that
   * needs a permission prompt, and a prompt nobody asked for on the screen
   * §11 says decides the funnel is a good way to lose someone. A refusal is
   * not a failure here — the map is already showing somewhere reasonable.
   */
  async function locate() {
    setLocating(true)
    try {
      const at = await getPosition()
      setCentre(at)
      setZoom(DEFAULT_ZOOM)
    } catch {
      // Denied or unavailable. The map stays where it is.
    } finally {
      setLocating(false)
    }
  }

  const label = [area?.postcode, area?.label].filter(Boolean).join(' ')

  return (
    <div>
      <div
        className="pin-map"
        onPointerDown={(e) => {
          // The controls live inside this frame, and capturing the pointer on
          // the container swallows their click entirely — which is why zoom
          // and locate did nothing. Let anything interactive have its event.
          if ((e.target as HTMLElement).closest('button')) return
          drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY }
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          const d = drag.current
          if (!d || d.id !== e.pointerId) return
          panBy(e.clientX - d.x, e.clientY - d.y)
          drag.current = { id: d.id, x: e.clientX, y: e.clientY }
        }}
        onPointerUp={(e) => {
          if (drag.current?.id === e.pointerId) drag.current = null
        }}
        onPointerCancel={() => {
          drag.current = null
        }}
      >
        <Tiles lat={centre.lat} lng={centre.lng} zoom={zoom} />

        {/* Fixed to the centre, above the tiles. The map moves under it, so
            there is no marker to drag and nothing to fight panning. */}
        <span className="pin-map-pin" aria-hidden>
          <svg viewBox="0 0 24 32" width="30" height="40" fill="none">
            <path
              d="M12 1c6.1 0 11 4.9 11 11 0 7.7-11 19-11 19S1 19.7 1 12C1 5.9 5.9 1 12 1z"
              fill="var(--accent)"
              stroke="var(--accent-ink)"
              strokeWidth="1.5"
            />
            <circle cx="12" cy="12" r="4" fill="var(--accent-ink)" />
          </svg>
        </span>

        <div className="pin-map-zoom">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 1))}
            disabled={zoom >= MAX_ZOOM}
            aria-label={t('area.zoomIn')}
          >
            <Plus size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 1))}
            disabled={zoom <= MIN_ZOOM}
            aria-label={t('area.zoomOut')}
          >
            <Minus size={16} aria-hidden />
          </button>
        </div>

        <button
          type="button"
          className="pin-map-locate"
          onClick={() => void locate()}
          disabled={locating}
          aria-label={t('area.locate')}
          title={t('area.locate')}
        >
          {locating ? <Spinner /> : <Navigation size={17} aria-hidden />}
        </button>

        <small className="pin-map-credit">© OpenStreetMap</small>
      </div>

      {/* What is under the pin, so the choice is legible before it is made. */}
      <p className="pin-where">
        {label ? (
          label
        ) : naming ? (
          <span className="faint">{t('area.naming')}</span>
        ) : (
          <span className="faint">{t('area.unnamed')}</span>
        )}
      </p>

      <button
        className="btn btn-primary"
        disabled={busy}
        onClick={() =>
          onPick(
            area ?? {
              // The lookup failed or has not landed. The pin is still the
              // area, so send it — the server names it again on its side.
              id: `@${centre.lat.toFixed(4)},${centre.lng.toFixed(4)}`,
              postcode: '',
              label: '',
              country: '',
            },
          )
        }
      >
        {busy ? <Spinner /> : <Check size={18} aria-hidden />}
        {t('area.confirm')}
      </button>
    </div>
  )
}

/**
 * The tile grid.
 *
 * Enough tiles to cover the frame plus a ring, so a drag reveals map rather
 * than background. Keyed by tile coordinates so React reuses the `<img>`
 * elements that have not moved — panning then redraws from cache instead of
 * re-requesting every tile.
 */
function Tiles({ lat, lng, zoom }: { lat: number; lng: number; zoom: number }) {
  const { x, y, offsetX, offsetY } = project(lat, lng, zoom)
  const span = [-2, -1, 0, 1, 2]
  const rows = 2 ** zoom

  return (
    <div
      className="pin-map-grid"
      style={{
        left: `calc(50% - ${offsetX}px - ${2 * TILE}px)`,
        top: `calc(50% - ${offsetY}px - ${2 * TILE}px)`,
        gridTemplateColumns: `repeat(${span.length}, ${TILE}px)`,
      }}
    >
      {span.map((dy) =>
        span.map((dx) => {
          const ty = y + dy
          // Nothing exists above the north pole or below the south one.
          if (ty < 0 || ty >= rows) {
            return (
              <span key={`gap-${dx},${dy}`} style={{ width: TILE, height: TILE }} />
            )
          }
          return (
            <img
              key={`${x + dx},${ty},${zoom}`}
              src={tileUrl(x + dx, ty, zoom)}
              width={TILE}
              height={TILE}
              alt=""
              draggable={false}
              decoding="async"
            />
          )
        }),
      )}
    </div>
  )
}
